/**
 * Reversi Board Component
 *
 * 8×8 grid with black/white discs, valid move indicators (dots),
 * flip animation via CSS transitions, and click-to-place interaction.
 */

import React, { useCallback } from 'react';
import {
  CellState,
  PlayerColor,
  Position,
  ReversiBoardProps,
  BOARD_SIZE,
} from '../types';
import './ReversiBoard.css';

const ReversiBoard: React.FC<ReversiBoardProps> = ({
  board,
  validMoves,
  lastFlipped,
  lastMove,
  currentPlayer,
  onCellClick,
  disabled = false,
  animating = false,
}) => {
  const isValidMove = useCallback(
    (row: number, col: number): boolean => {
      return validMoves.some(m => m.row === row && m.col === col);
    },
    [validMoves]
  );

  const isLastFlipped = useCallback(
    (row: number, col: number): boolean => {
      return lastFlipped.some(p => p.row === row && p.col === col);
    },
    [lastFlipped]
  );

  const isLastMove = useCallback(
    (row: number, col: number): boolean => {
      return lastMove ? lastMove.row === row && lastMove.col === col : false;
    },
    [lastMove]
  );

  const handleCellClick = useCallback(
    (row: number, col: number) => {
      if (disabled || animating) return;
      if (!isValidMove(row, col)) return;
      onCellClick({ row, col });
    },
    [disabled, animating, isValidMove, onCellClick]
  );

  const renderCell = (row: number, col: number) => {
    const cell = board[row][col];
    const valid = isValidMove(row, col);
    const flipped = isLastFlipped(row, col);
    const lastPlaced = isLastMove(row, col);

    const cellClasses = [
      'reversi-board__cell',
      valid && !disabled ? 'reversi-board__cell--valid' : '',
      lastPlaced ? 'reversi-board__cell--last-move' : '',
    ]
      .filter(Boolean)
      .join(' ');

    const discClasses = [
      'reversi-board__disc',
      cell !== 'empty' ? `reversi-board__disc--${cell}` : '',
      flipped ? 'reversi-board__disc--flipped' : '',
      lastPlaced ? 'reversi-board__disc--placed' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <button
        key={`${row}-${col}`}
        className={cellClasses}
        onClick={() => handleCellClick(row, col)}
        disabled={disabled || !valid}
        aria-label={getCellAriaLabel(row, col, cell, valid, currentPlayer)}
        role="gridcell"
        tabIndex={valid && !disabled ? 0 : -1}
      >
        {cell !== 'empty' && (
          <span className={discClasses} aria-hidden="true" />
        )}
        {cell === 'empty' && valid && !disabled && (
          <span className="reversi-board__valid-dot" aria-hidden="true" />
        )}
      </button>
    );
  };

  return (
    <div
      className="reversi-board"
      role="grid"
      aria-label="Reversi game board"
    >
      {/* Column labels */}
      <div className="reversi-board__col-labels" aria-hidden="true">
        <span className="reversi-board__corner-label" />
        {Array.from({ length: BOARD_SIZE }, (_, i) => (
          <span key={i} className="reversi-board__col-label">
            {String.fromCharCode(65 + i)}
          </span>
        ))}
      </div>

      {/* Board rows */}
      {Array.from({ length: BOARD_SIZE }, (_, row) => (
        <div key={row} className="reversi-board__row" role="row">
          <span className="reversi-board__row-label" aria-hidden="true">
            {row + 1}
          </span>
          {Array.from({ length: BOARD_SIZE }, (_, col) => renderCell(row, col))}
        </div>
      ))}
    </div>
  );
};

function getCellAriaLabel(
  row: number,
  col: number,
  cell: CellState,
  isValid: boolean,
  currentPlayer: PlayerColor
): string {
  const colLetter = String.fromCharCode(65 + col);
  const position = `${colLetter}${row + 1}`;

  if (cell !== 'empty') {
    return `${position}: ${cell} disc`;
  }
  if (isValid) {
    return `${position}: valid move for ${currentPlayer}`;
  }
  return `${position}: empty`;
}

export { ReversiBoard };
export default ReversiBoard;
